/**
 * Single place for audience person-CV tuning (COCO-SSD + IOU tracker + motion gate).
 * Adjust here rather than scattering magic numbers across the pipeline.
 *
 * When tracker behaviour changes meaningfully, document in
 * `packages/shared/models/audience-heartbeat-aggregate-semantics.md` and note the player release.
 */
export const audienceCvPersonConfig = {
  /** Reported on heartbeat aggregates and stored samples (audit trail). */
  modelId: 'coco-ssd-mobilenet_v2',
  detect: {
    /** Ignore video frames smaller than this (px) for detection. */
    minVideoExtentPx: 64,
    /** Minimum COCO-SSD class score to keep a “person” box. */
    minScore: 0.45,
    /** Min time between COCO-SSD runs on the capture video (ms). */
    sampleIntervalMs: 3_000
  },
  tracker: {
    iouMatchThreshold: 0.35,
    maxMissedFrames: 2,
    minTrackSpanMs: 2_500,
    minHitCount: 2,
    /**
     * Max simultaneous IOU tracks after each frame (pathological detector spam / crowded scenes).
     * Keeps CPU and memory bounded on-device (S2).
     */
    maxConcurrentTracks: 48
  },
  /**
   * Skip an expensive CV tick when luma motion in the current window looks idle.
   * Trade-off: very still people may be missed for that tick; saves CPU in empty scenes.
   */
  motionGate: {
    /** Need this many luma-diff samples before applying the gate (avoids skipping early in the window). */
    minDiffSamples: 8,
    /**
     * Skip CV when every diff was below the motion “active” threshold and mean luma diff is below this.
     */
    maxMeanMotionWithZeroActive: 0.01,
    /** Skip CV when at most this fraction of diffs were “active” and mean motion is still low. */
    maxPresenceFractionForSkip: 0.05,
    maxMeanMotionWhenLowPresence: 0.012
  }
} as const

export type TAudienceCvPersonConfig = typeof audienceCvPersonConfig

/**
 * Whether to skip scheduling `detectPersonBoxesFromVideo` for this tick based on running-window motion stats.
 */
export function shouldSkipAudienceCvByMotion(
  motionCount: number,
  motionSum: number,
  activeSamples: number,
  gate: TAudienceCvPersonConfig['motionGate'] = audienceCvPersonConfig.motionGate
): boolean {
  if (motionCount < gate.minDiffSamples) {
    return false
  }
  const mean = motionSum / motionCount
  const presenceFrac = activeSamples / motionCount
  if (activeSamples === 0 && mean < gate.maxMeanMotionWithZeroActive) {
    return true
  }
  if (presenceFrac <= gate.maxPresenceFractionForSkip && mean < gate.maxMeanMotionWhenLowPresence) {
    return true
  }
  return false
}

/**
 * jsDelivr fallback when same-origin `public/tfjs-wasm/` is empty or blocked.
 * Keep aligned with `@tensorflow/tfjs-backend-wasm` / `@tensorflow/tfjs` in package.json.
 */
export const audienceCvTfjsWasmCdnVersion = '4.22.0'
